<template>
  <ModalLayout :title="isEdit ? 'Edit Container Type' : 'Add Container Type'">
    <div
      class="relative rounded-t-[10px] bg-white px-[24px] pt-[41px] sm:px-[40px]"
    >
      <button
        type="button"
        aria-label="Close"
        class="absolute right-[24px] top-[36px] flex size-[32px] cursor-pointer items-center justify-center rounded-full border border-gpx_gray-800 sm:right-[40px]"
        @click.prevent="emit('close')"
      >
        <XMarkIcon
          class="heroicon-stroke-w-1.4 size-[20px] text-gpx_gray-200"
        />
      </button>

      <div class="border-b border-b-gpx_gray-800 pb-[18px]">
        <div class="mb-[2px] text-gpx_sm font-medium text-gpx_gray-200">
          Container Types
        </div>
        <div class="text-gpx_2xl font-semibold text-gpx_black-500">
          {{ isEdit ? 'Edit this type' : 'Add a type' }}
        </div>
      </div>

      <form class="mt-[35px]" @submit.prevent="handleSave">
        <UIInput v-model="name" label="Name" placeholder="e.g. Steel rack" />
        <p class="mt-[4px] text-gpx_xs text-gpx_gray-100">
          {{
            isEdit
              ? 'Every tracker carrying this type follows the new name, and so does its history.'
              : 'Trackers are assigned a type from their own page.'
          }}
        </p>
        <UIInput
          v-model="value"
          class="mt-[24px]"
          label="Value"
          placeholder="e.g. 1,250"
          :hasError="!!valueError"
        />
        <p
          class="mt-[4px] text-gpx_xs"
          :class="valueError ? 'text-gpx_red-200' : 'text-gpx_gray-100'"
        >
          {{
            valueError ||
            'What one container of this type is worth, in whole US dollars. Optional.'
          }}
        </p>
      </form>
    </div>

    <div class="rounded-b-[10px] bg-white px-[24px] py-[32px] sm:px-[40px]">
      <Button
        size="lg"
        loadingText="Saving"
        :loading="isSaving"
        :disabled="isSaving || !name.trim()"
        @click="handleSave"
      >
        {{ isEdit ? 'Save' : 'Add container type' }}
      </Button>
    </div>
  </ModalLayout>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { Button } from '@gpx-spa/ui';
import {
  createAccountContainerType,
  updateAccountContainerType,
} from '@gpx-spa/queries';
import ModalLayout from '@/layouts/ModalLayout.vue';
import UIInput from '@/components/ui/UIInput.vue';
import {
  containerTypeValueInput,
  parseContainerTypeValue,
} from '@/utils/container-type-value';

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const toast = useToast();
const createMutation = createAccountContainerType();
const updateMutation = updateAccountContainerType();

const accountId = computed(() => store.getters['auth/accountId']);
const isEdit = computed(() => !!props.params?.id);

const name = ref(props.params?.name ?? '');
const value = ref(containerTypeValueInput(props.params?.value));
const valueError = ref('');
const isSaving = ref(false);

watch(value, () => {
  valueError.value = '';
});

const handleSave = async () => {
  const trimmed = name.value.trim();
  if (isSaving.value || !trimmed) return;

  const parsed = parseContainerTypeValue(value.value);
  if (parsed.error) {
    valueError.value = parsed.error;
    return;
  }

  isSaving.value = true;
  try {
    if (isEdit.value) {
      await updateMutation.mutateAsync({
        accountId: accountId.value,
        id: props.params.id,
        name: trimmed,
        value: parsed.cents,
      });
      toast.success('Container type updated');
    } else {
      await createMutation.mutateAsync({
        accountId: accountId.value,
        name: trimmed,
        value: parsed.cents,
      });
      toast.success('Container type added');
    }
    emit('close');
  } catch (e) {
    const serverValueError = e.response?.data?.errors?.value?.[0];
    if (serverValueError) {
      valueError.value = serverValueError;
      return;
    }
    toast.error(
      e.response?.data?.message ||
        'Could not save the container type. Please try again later.',
    );
  } finally {
    isSaving.value = false;
  }
};
</script>
